import { useCallback } from "react";

import { CategoryAndTag } from "api/generated/model";
import { DateRange } from "components/filters/dateRange";
import { ExistingControlActivityStatus } from "utils/controlResults/activityStatusUtils";

import { buildCanonicalActivitiesUrl } from "../utils/activitiesUrl";

import { useActivitiesUrlDependencies } from "./useActivitiesUrlDependencies";

interface UseBuildActivitiesUrlProps {
    dateRange: DateRange | null;
    filteredControlProgramIds: Array<number>;
    filteredTags: Array<CategoryAndTag>;
    filteredEntityIds: Array<number>;
    showArchivedActivities: boolean;
    showInactivePrograms: boolean;
}

/**
 * Returns a function configured with the provided filters that receives a status
 * key and returns the corresponding activities page URL, with all the
 * dashboard filters applied as a temporary, non-persisted (`noSave`) filter.
 */
export function useBuildActivitiesUrl({
    dateRange,
    filteredControlProgramIds,
    filteredTags,
    filteredEntityIds,
    showArchivedActivities,
    showInactivePrograms,
}: UseBuildActivitiesUrlProps): (
    statusKey: ExistingControlActivityStatus | null
) => string {
    const dependencies = useActivitiesUrlDependencies();

    return useCallback(
        (statusKey: ExistingControlActivityStatus | null) =>
            buildCanonicalActivitiesUrl(
                {
                    dateRange,
                    statusKey,
                    filteredControlProgramIds,
                    filteredTags,
                    filteredEntityIds,
                    showArchivedActivities,
                    showInactivePrograms,
                },
                dependencies
            ),
        [
            dateRange,
            filteredControlProgramIds,
            filteredTags,
            filteredEntityIds,
            showArchivedActivities,
            showInactivePrograms,
            dependencies,
        ]
    );
}
